import PlotlyChart from 'AnalyticsDashboard/components/PlotlyChart';

export default function ConfidenceIntervalLineChart({ data, chartOptions, query }) {
  const { columnMapping, normalize = false } = chartOptions;
  const { secondaryDimension } = query;

  // Group data by secondary dimension, or use all data if no dimension
  let groupedData = {};

  if (!secondaryDimension?.id) {
    groupedData = { 'All Data': data };
  } else {
    _.orderBy(data, 'hour').forEach(row => {
      const dimensionValue = row[secondaryDimension.id] || '(Not specified)';
      if (!groupedData[dimensionValue]) {
        groupedData[dimensionValue] = [];
      }
      groupedData[dimensionValue].push(row);
    });
  }

  // Sort dimension values for consistent ordering
  const dimensionValues = Object.keys(groupedData).sort((a, b) => {
    if (a === 'All Data') return -1;
    if (b === 'All Data') return 1;
    if (a === '(Not specified)') return 1;
    if (b === '(Not specified)') return -1;
    return a.localeCompare(b);
  });

  const renderChart = (dimensionValue, chartData) => {
    const hours = chartData.map(d => d.hour);
    const actualValues = chartData.map(d => d[columnMapping.valueColumn]);
    const expectedValues = chartData.map(d => d[columnMapping.meanColumn]);

    const upperBound = chartData.map(d => {
      return d[columnMapping.meanColumn] + d[columnMapping.confidenceIntervalColumn];
    });

    const lowerBound = chartData.map(d => {
      return d[columnMapping.meanColumn] - d[columnMapping.confidenceIntervalColumn];
    });

    const normalizedExpectedValues = expectedValues.map(v => 0);
    const normalizedActualValues = chartData.map(d => {
      if (d[columnMapping.meanColumn] === 0) return 0;
      return (d[columnMapping.valueColumn] - d[columnMapping.meanColumn]) / d[columnMapping.meanColumn];
    });

    const normalizedUpperBound = upperBound.map((v, i) => {
      if (expectedValues[i] === 0) return 0;
      return (v - expectedValues[i]) / expectedValues[i];
    });

    const normalizedLowerBound = lowerBound.map((v, i) => {
      if (expectedValues[i] === 0) return 0;
      return (v - expectedValues[i]) / expectedValues[i];
    });

    const getPointColor = (actual, expected, lower, upper) => {
      if (actual < lower) return 'rgb(220, 38, 38)';
      if (actual < expected) return 'rgb(249, 115, 22)';
      if (actual <= upper) return 'rgb(234, 179, 8)';
      return 'rgb(34, 197, 94)';
    };

    const markerColors = chartData.map((d, i) => {
      const actual = normalize ? normalizedActualValues[i] : actualValues[i];
      const expected = normalize ? normalizedExpectedValues[i] : expectedValues[i];
      const lower = normalize ? normalizedLowerBound[i] : lowerBound[i];
      const upper = normalize ? normalizedUpperBound[i] : upperBound[i];
      return getPointColor(actual, expected, lower, upper);
    });

    const traces = [
      {
        x: hours,
        y: normalize ? normalizedUpperBound : upperBound,
        type: 'scatter',
        mode: 'lines',
        name: '95% Confidence Interval',
        line: { width: 0 },
        fillcolor: 'rgba(99, 110, 250, 0.15)',
        hoverinfo: 'skip',
        showlegend: false,
      },
      {
        x: hours,
        y: normalize ? normalizedLowerBound : lowerBound,
        type: 'scatter',
        mode: 'lines',
        name: 'Lower Bound',
        line: { width: 0 },
        fillcolor: 'rgba(99, 110, 250, 0.15)',
        fill: 'tonexty',
        hoverinfo: 'skip',
        showlegend: false,
      },
      {
        x: hours,
        y: normalize ? normalizedExpectedValues : expectedValues,
        type: 'scatter',
        mode: 'lines',
        name: 'Expected Opens',
        line: {
          color: 'rgb(99, 110, 250)',
          width: 2,
          dash: 'dash'
        },
        hovertemplate: '<b>Hour %{x}</b><br>Expected: %{y:.1f}<extra></extra>',
        showlegend: false
      },
      {
        x: hours,
        y: normalize ? normalizedActualValues : actualValues,
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Actual Opens',
        line: {
          color: 'rgba(107, 114, 128, 0.3)',
          width: 2
        },
        marker: {
          size: 10,
          color: markerColors,
          line: {
            color: 'white',
            width: 2
          }
        },
        hovertemplate: '<b>Hour %{x}</b><br>Actual: %{y}<extra></extra>',
        showlegend: false
      },
    ];

    const layout = {
      title: {
        text: `${dimensionValue}`,
        font: {
          size: 18,
          color: '#1f2937',
          family: 'Inter, system-ui, sans-serif'
        }
      },
      xaxis: {
        title: {
          text: 'Hours Since Send',
          font: { size: 14, color: '#4b5563' }
        },
        gridcolor: '#e5e7eb',
        linecolor: '#d1d5db',
        tickfont: { color: '#6b7280' }
      },
      yaxis: {
        title: {
          text: normalize ? 'Difference from Benchmark' : 'Opens',
          font: { size: 14, color: '#4b5563' }
        },
        tickformat: normalize ? '.0%' : undefined,
        gridcolor: '#e5e7eb',
        linecolor: '#d1d5db',
        tickfont: { color: '#6b7280' }
      },
      plot_bgcolor: '#ffffff',
      paper_bgcolor: '#f9fafb',
      hovermode: 'x unified',
      showlegend: false,
      margin: { l: 60, r: 40, t: 60, b: 60 },
    };

    const plotConfig = {
      responsive: true,
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
    };

    return (
      <div key={dimensionValue} className="bg-white rounded-lg shadow p-4 mb-4">
        <PlotlyChart
          data={traces}
          layout={layout}
          config={plotConfig}
          style={{ width: '100%', height: '200px' }}
        />
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {secondaryDimension?.label ? `Email Opens by ${secondaryDimension.label}` : 'Email Opens: Actual vs Expected'}
          </h2>
          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Interpretation:</strong> The shaded area represents the 95% confidence interval (mean ± 1.96 × standard deviation).</p>
            <p><strong>Point colors:</strong> <span className="text-red-600 font-semibold">Red</span> (below CI), <span className="text-orange-500 font-semibold">Orange</span> (lower half), <span className="text-yellow-500 font-semibold">Yellow</span> (upper half), <span className="text-green-600 font-semibold">Green</span> (above CI).</p>
          </div>
        </div>

        <div className="space-y-4">
          {dimensionValues.map(dimensionValue =>
            renderChart(dimensionValue, groupedData[dimensionValue])
          )}
        </div>
      </div>
    </div>
  );
}
